import type { ExtractionData, FieldProvenance, Section } from '@/lib/scraper'
import { fetchScreenshot } from '@/lib/scraper'
import { Copy, ImageOff, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { toast } from 'sonner'
import {
  CARD,
  CHIP_OFF,
  CHIP_ON,
  MUTED,
  PILL,
  SECTION_FALLBACK,
  SECTION_STYLE,
  SKELETON,
  cls
} from './ui'

export interface ViewPage {
  url: string
  final_url?: string
  status_code?: number | null
  tier?: string
  page_role?: string
  duration_ms?: number
  captcha?: { kind: string; solved: boolean; solver?: string | null } | null
  flags?: string[]
  error?: string
  markdown?: string
  text?: string
  html?: string
  raw_html?: string
  links?: { url: string; text: string }[]
  metadata?: Record<string, unknown>
  sections?: Section[]
  screenshot?: string | null
}

type Tab = 'overview' | 'markdown' | 'json' | 'links' | 'metadata' | 'html'

const TIER_LABEL: Record<string, string> = {
  http: 'HTTP',
  dynamic: 'Browser',
  stealth: 'Stealth browser',
  vision: 'Vision'
}

export function ResultView({
  page,
  extractions
}: {
  page: ViewPage
  extractions: ExtractionData[]
}) {
  const [tab, setTab] = useState<Tab>('overview')
  const tabs: { id: Tab; label: string; show: boolean }[] = [
    { id: 'overview', label: 'Overview', show: true },
    { id: 'markdown', label: 'Markdown', show: !!page.markdown },
    {
      id: 'json',
      label: `JSON (${extractions.length})`,
      show: extractions.length > 0
    },
    {
      id: 'links',
      label: `Links (${page.links?.length ?? 0})`,
      show: !!page.links?.length
    },
    { id: 'metadata', label: 'Metadata', show: !!page.metadata },
    { id: 'html', label: 'HTML', show: !!(page.html || page.raw_html) }
  ]

  return (
    <div className="space-y-4">
      <Summary page={page} />
      <div
        role="tablist"
        aria-label="Result views"
        className="flex flex-wrap gap-2"
      >
        {tabs
          .filter((t) => t.show)
          .map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={tab === t.id ? CHIP_ON : CHIP_OFF}
            >
              {t.label}
            </button>
          ))}
      </div>
      {tab === 'overview' && <Overview page={page} />}
      {tab === 'markdown' && (
        <div
          className={cls(
            CARD,
            'prose prose-sm max-w-none overflow-x-auto dark:prose-invert'
          )}
        >
          <CopyButton text={page.markdown ?? ''} />
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {page.markdown ?? ''}
          </ReactMarkdown>
        </div>
      )}
      {tab === 'json' && (
        <div className="space-y-4">
          {extractions.map((e) => (
            <ExtractionCard key={e.recipe} extraction={e} />
          ))}
        </div>
      )}
      {tab === 'links' && (
        <div className={cls(CARD, 'max-h-[520px] overflow-y-auto')}>
          <ul className="space-y-1.5 text-xs">
            {page.links?.map((l) => (
              <li key={l.url} className="flex gap-3">
                <span className="w-40 shrink-0 truncate text-neutral-500 dark:text-neutral-400">
                  {l.text || '(no text)'}
                </span>
                <a
                  href={l.url}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate font-mono text-neutral-900 underline-offset-2 hover:underline dark:text-white"
                >
                  {l.url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {tab === 'metadata' && <JsonBlock value={page.metadata} />}
      {tab === 'html' && (
        <pre
          className={cls(
            CARD,
            'max-h-[520px] overflow-auto whitespace-pre-wrap font-mono text-[11px] text-neutral-700 dark:text-neutral-300'
          )}
        >
          {page.html || page.raw_html}
        </pre>
      )}
    </div>
  )
}

function Summary({ page }: { page: ViewPage }) {
  const solved = page.captcha?.solved
  return (
    <div className={cls(CARD, 'flex flex-wrap items-center gap-2')}>
      <a
        href={page.final_url || page.url}
        target="_blank"
        rel="noreferrer"
        className="mr-auto min-w-0 truncate text-sm font-semibold text-neutral-900 hover:underline dark:text-white"
      >
        {page.final_url || page.url}
      </a>
      {page.tier && (
        <span className={PILL}>{TIER_LABEL[page.tier] ?? page.tier}</span>
      )}
      {page.status_code != null && (
        <span className={PILL}>HTTP {page.status_code}</span>
      )}
      {page.page_role && <span className={PILL}>{page.page_role}</span>}
      {page.duration_ms ? (
        <span className={PILL}>{(page.duration_ms / 1000).toFixed(1)}s</span>
      ) : null}
      {page.captcha && (
        <span
          className={cls(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
            solved
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-red-500/10 text-red-600 dark:text-red-400'
          )}
        >
          {solved ? (
            <ShieldCheck className="size-3" />
          ) : (
            <ShieldAlert className="size-3" />
          )}
          {page.captcha.kind}{' '}
          {solved ? `solved (${page.captcha.solver})` : 'not solved'}
        </span>
      )}
      {page.flags?.map((f) => (
        <span
          key={f}
          className="inline-flex rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400"
        >
          {f.replaceAll('_', ' ')}
        </span>
      ))}
      {page.error && (
        <p className="w-full text-xs text-red-500">{page.error}</p>
      )}
    </div>
  )
}

function Overview({ page }: { page: ViewPage }) {
  const sections = useMemo(
    () =>
      (page.sections ?? []).filter(
        (s) => s.kind !== 'navigation' && s.kind !== 'footer'
      ),
    [page.sections]
  )
  const [active, setActive] = useState<number | null>(null)
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
      <Screenshot
        path={page.screenshot ?? null}
        sections={page.sections ?? []}
        active={active}
        onHover={setActive}
      />
      <div className={cls(CARD, 'max-h-[720px] space-y-2 overflow-y-auto')}>
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
          Sections found ({sections.length})
        </h3>
        {sections.length === 0 && (
          <p className={MUTED}>
            No sections detected. Add “sections” to the formats to see them.
          </p>
        )}
        {sections.map((s) => (
          <button
            key={`${s.order}-${s.kind}`}
            type="button"
            onMouseEnter={() => setActive(s.order)}
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(s.order)}
            onBlur={() => setActive(null)}
            className={cls(
              'block w-full rounded-[24px] border p-3 text-left transition-colors',
              active === s.order
                ? 'border-neutral-900 bg-neutral-50 dark:border-white dark:bg-neutral-800'
                : 'border-neutral-200 bg-white hover:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-neutral-600'
            )}
          >
            <div className="flex items-center gap-2">
              <span
                className={cls(
                  'size-2.5 rounded-full',
                  (SECTION_STYLE[s.kind] ?? SECTION_FALLBACK).dot
                )}
              />
              <span className="text-xs font-bold uppercase tracking-wide text-neutral-900 dark:text-white">
                {s.kind}
              </span>
              <span className="ml-auto text-[11px] text-neutral-500 dark:text-neutral-400">
                {s.method} · {Math.round(s.confidence * 100)}%
              </span>
            </div>
            {s.heading && (
              <p className="mt-1 truncate text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                {s.heading}
              </p>
            )}
            <p className="mt-0.5 line-clamp-2 text-[11px] text-neutral-500 dark:text-neutral-400">
              {s.text}
            </p>
          </button>
        ))}
      </div>
    </div>
  )
}

function Screenshot({
  path,
  sections,
  active,
  onHover
}: {
  path: string | null
  sections: Section[]
  active: number | null
  onHover: (order: number | null) => void
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [size, setSize] = useState<{ w: number; h: number } | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle')

  useEffect(() => {
    if (!path) return
    let url: string | null = null
    let alive = true
    setState('loading')
    fetchScreenshot(path)
      .then((u) => {
        url = u
        if (alive) {
          setSrc(u)
          setState('idle')
        }
      })
      .catch(() => alive && setState('error'))
    return () => {
      alive = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [path])

  if (!path || state === 'error') {
    return (
      <div
        className={cls(
          CARD,
          'flex min-h-[240px] flex-col items-center justify-center gap-2 text-center'
        )}
      >
        <ImageOff className="size-5 text-neutral-400" />
        <p className={MUTED}>
          {state === 'error'
            ? 'The screenshot could not be loaded.'
            : 'Add “screenshot” to the formats to see the page with its sections drawn on it.'}
        </p>
      </div>
    )
  }
  if (state === 'loading' || !src)
    return <div className={cls(SKELETON, 'h-[520px] w-full')} />

  const boxed = sections.filter(
    (s) => s.bbox && s.kind !== 'navigation' && s.kind !== 'footer'
  )
  return (
    <div className="max-h-[720px] overflow-y-auto rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
      <div className="relative">
        <img
          src={src}
          alt="Full page screenshot"
          className="block w-full"
          onLoad={(e) =>
            setSize({
              w: e.currentTarget.naturalWidth,
              h: e.currentTarget.naturalHeight
            })
          }
        />
        {size &&
          boxed.map((s) => {
            const [x, y, w, h] = s.bbox as number[]
            const style = SECTION_STYLE[s.kind] ?? SECTION_FALLBACK
            const on = active === null || active === s.order
            return (
              <div
                key={`${s.order}-${s.kind}`}
                onMouseEnter={() => onHover(s.order)}
                onMouseLeave={() => onHover(null)}
                className={cls(
                  'absolute transition-opacity motion-reduce:transition-none',
                  style.box
                )}
                style={{
                  left: `${(x / size.w) * 100}%`,
                  top: `${(y / size.h) * 100}%`,
                  width: `${(w / size.w) * 100}%`,
                  height: `${(h / size.h) * 100}%`,
                  opacity: on ? 1 : 0.15
                }}
              >
                <span
                  className={cls(
                    'absolute left-0 top-0 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white',
                    style.tag
                  )}
                >
                  {s.kind}
                </span>
              </div>
            )
          })}
      </div>
    </div>
  )
}

function ExtractionCard({ extraction }: { extraction: ExtractionData }) {
  const items = Array.isArray(extraction.data.items)
    ? (extraction.data.items as Record<string, unknown>[])
    : null
  return (
    <div className={cls(CARD, 'space-y-3')}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-bold capitalize text-neutral-900 dark:text-white">
          {extraction.recipe}
        </h3>
        <span className={PILL}>
          {Math.round(extraction.confidence * 100)}% confidence
        </span>
        <span
          className={cls(
            'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
            extraction.meets_required_fields
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
          )}
        >
          {extraction.meets_required_fields
            ? 'required fields met'
            : 'missing required fields'}
        </span>
        <div className="ml-auto">
          <CopyButton text={JSON.stringify(extraction.data, null, 2)} />
        </div>
      </div>
      {extraction.error && (
        <p className="text-xs text-red-500">{extraction.error}</p>
      )}
      {items ? (
        <ItemsTable items={items} />
      ) : (
        <FieldTable
          data={extraction.data}
          provenance={extraction.provenance as Record<string, FieldProvenance>}
        />
      )}
    </div>
  )
}

function FieldTable({
  data,
  provenance
}: {
  data: Record<string, unknown>
  provenance: Record<string, FieldProvenance>
}) {
  const keys = Object.keys(data)
  if (!keys.length)
    return <p className={MUTED}>Nothing supported by the page was found.</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-neutral-200 text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
            <th className="py-2 pr-3 font-semibold">Field</th>
            <th className="py-2 pr-3 font-semibold">Value</th>
            <th className="py-2 font-semibold">Where it came from</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const p = provenance[k]
            return (
              <tr
                key={k}
                className="border-b border-neutral-100 align-top dark:border-neutral-800/60"
              >
                <td className="py-2 pr-3 font-mono text-neutral-500 dark:text-neutral-400">
                  {k}
                </td>
                <td className="py-2 pr-3 text-neutral-900 dark:text-white">
                  <Value value={data[k]} />
                </td>
                <td className="py-2 text-neutral-500 dark:text-neutral-400">
                  {p ? (
                    <>
                      <span className="font-semibold text-neutral-700 dark:text-neutral-300">
                        {p.method}
                      </span>{' '}
                      · {Math.round((p.confidence ?? 0) * 100)}%
                      {p.alternatives && p.alternatives.length > 0 && (
                        <span className="block text-amber-600 dark:text-amber-400">
                          also saw:{' '}
                          {p.alternatives
                            .map((a) => String(a.value))
                            .join(', ')}
                        </span>
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function ItemsTable({ items }: { items: Record<string, unknown>[] }) {
  if (!items.length) return <p className={MUTED}>No items found.</p>
  const columns = Array.from(
    new Set(
      items
        .flatMap((i) => Object.keys(i))
        .filter((k) => !k.startsWith('_') || k === '_method')
    )
  ).slice(0, 8)
  return (
    <div className="max-h-[480px] overflow-auto">
      <table className="w-full text-left text-xs">
        <thead className="sticky top-0 bg-white dark:bg-neutral-900">
          <tr className="border-b border-neutral-200 text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
            {columns.map((c) => (
              <th key={c} className="py-2 pr-3 font-semibold">
                {c === '_method' ? 'source' : c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr
              key={i}
              className="border-b border-neutral-100 align-top dark:border-neutral-800/60"
            >
              {columns.map((c) => (
                <td
                  key={c}
                  className="max-w-[240px] py-2 pr-3 text-neutral-900 dark:text-white"
                >
                  <Value value={item[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Value({ value }: { value: unknown }) {
  if (value == null || value === '')
    return <span className="text-neutral-400">—</span>
  if (typeof value === 'string' && /^https?:\/\//.test(value)) {
    return /\.(jpe?g|png|webp|gif)(\?|$)/i.test(value) ? (
      <img
        src={value}
        alt=""
        className="size-10 rounded-full object-cover"
        loading="lazy"
      />
    ) : (
      <a
        href={value}
        target="_blank"
        rel="noreferrer"
        className="break-all underline-offset-2 hover:underline"
      >
        {value}
      </a>
    )
  }
  if (typeof value === 'object') {
    return (
      <pre className="whitespace-pre-wrap font-mono text-[11px]">
        {JSON.stringify(value, null, 1)}
      </pre>
    )
  }
  return <span className="line-clamp-3">{String(value)}</span>
}

export function JsonBlock({ value }: { value: unknown }) {
  const text = JSON.stringify(value ?? {}, null, 2)
  return (
    <div className={cls(CARD, 'relative')}>
      <div className="absolute right-4 top-4">
        <CopyButton text={text} />
      </div>
      <pre className="max-h-[520px] overflow-auto whitespace-pre-wrap font-mono text-[11px] text-neutral-700 dark:text-neutral-300">
        {text}
      </pre>
    </div>
  )
}

function CopyButton({ text }: { text: string }) {
  const [busy, setBusy] = useState(false)
  return (
    <button
      type="button"
      aria-label="Copy"
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        try {
          await navigator.clipboard.writeText(text)
          toast.success('Copied')
        } catch {
          toast.error('Could not copy to the clipboard')
        } finally {
          setBusy(false)
        }
      }}
      className="grid size-8 place-items-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-100 disabled:opacity-50 dark:text-neutral-400 dark:hover:bg-neutral-800"
    >
      {busy ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Copy className="size-4" />
      )}
    </button>
  )
}
